import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { clearBeds, getBeds } from 'features/accommodation/accommodationSlice'
import { IGetAllBedsSingleResponse } from 'types/api/accommodationApi.interface'
import { ISelectValue } from 'constants/globalTypes'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { JustSpinner } from 'components/Loading/Loading'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { Container, ContainerContent, ContainerHeader } from '../Accommodation.style'
import { TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { formatDateForDisplay } from 'utils/helpers'
import { BedIcon } from '../icons/BedIcon'
import { OccupiedBedIcon } from '../icons/OccupiedBedIcon'
import { LocationIcon } from '../icons/LocationIcon'
import { NextIcon } from '../icons/NextIcon'
import {
  ControlsRow,
  Caption,
  KpiRow,
  CampSummaryRow,
  CampSummaryChip,
  CampSummaryCount,
  ResultStack,
  CampGroupTitle,
  CampGroupName,
  RoomGroup,
  RoomGroupHead,
  RoomGroupName,
  RoomGroupContext,
  BedGrid,
  BedCard,
  BedCardTop,
  BedCardLabel,
  BedCardLocation,
  PolicyPills,
  BedCardActions,
  EmptyState,
  LoaderWrap,
} from './AccommodationAvailability.styled'
import { AssignBedModal } from './AssignBedModal'
import { IAssignBedTarget } from 'types/accommodation.interface'

const genderOptions = [
  { value: 1, label: 'Male' },
  { value: 2, label: 'Female' },
]

const tierOptions = [
  { value: 1, label: 'VIP' },
  { value: 2, label: 'Senior' },
  { value: 3, label: 'Junior' },
  { value: 4, label: 'Contractor' },
]

const genderLabel = (g: number) => (g === 1 ? 'Male' : g === 2 ? 'Female' : g === 3 ? 'Both' : 'Any')
const tierLabel = (t: number) =>
  t === 1 ? 'VIP' : t === 2 ? 'Senior' : t === 3 ? 'Junior' : t === 4 ? 'Contractor' : 'Any'

interface RoomBucket {
  roomName: string
  blockName: string
  gender: number
  tier: number
  beds: IGetAllBedsSingleResponse[]
}

interface CampBucket {
  campName: string
  count: number
  rooms: RoomBucket[]
}

export const AccommodationAvailability = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { beds, isLoading } = useSelector((state: RootState) => state.accomodation)

  const [from, setFrom] = useState<Date | null>(null)
  const [to, setTo] = useState<Date | null>(null)
  const [gender, setGender] = useState<number | null>(null)
  const [tier, setTier] = useState<number | null>(null)
  const [searched, setSearched] = useState(false)
  const [range, setRange] = useState<{ from: Date; to: Date } | null>(null)
  const [assignBed, setAssignBed] = useState<IAssignBedTarget | null>(null)

  const available = beds?.data || []

  const grouped = useMemo(() => {
    const campMap = new Map<string, CampBucket>()
    available.forEach((bed) => {
      const campName = bed.campName || 'Unassigned'
      const roomName = bed.roomName || '/'
      if (!campMap.has(campName)) {
        campMap.set(campName, { campName, count: 0, rooms: [] })
      }
      const camp = campMap.get(campName)
      camp.count += 1
      let room = camp.rooms.find((r) => r.roomName === roomName && r.blockName === bed.blockName)
      if (!room) {
        room = {
          roomName,
          blockName: bed.blockName || '/',
          gender: bed.blockGenderPolicy,
          tier: bed.blockTierPolicy,
          beds: [],
        }
        camp.rooms.push(room)
      }
      room.beds.push(bed)
    })
    return Array.from(campMap.values())
  }, [available])

  const runSearch = (f: Date, t: Date) => {
    dispatch(
      getBeds({
        page: 1,
        limit: 999,
        checkInTimestamp: f.toISOString(),
        expectedCheckOutTimestamp: t.toISOString(),
        genderPolicy: gender,
        tierPolicy: tier,
      }),
    )
  }

  const handleCheck = () => {
    if (!from || !to) return
    setRange({ from, to })
    setSearched(true)
    runSearch(from, to)
  }

  const closeAssign = (assigned?: boolean) => {
    if (assigned && range) runSearch(range.from, range.to)
    setAssignBed(null)
  }

  const resetTo = () => {
    setTo(null)
  }

  const onFromChange = (date: Date | null) => {
    setFrom(date)
    if (date && to && to.getTime() < date.getTime()) resetTo()
  }

  const clearResults = () => {
    setSearched(false)
    setRange(null)
    dispatch(clearBeds())
  }

  return (
    <>
      <Container>
        <ContainerHeader padding>
          <TitleText>Check availability</TitleText>
        </ContainerHeader>
        <ContainerContent noHeader>
          <ControlsRow>
            <CalendarInput
              nomarg
              labelText='From'
              minDate={new Date()}
              defaultValue={from}
              onChange={(date: Date | null) => onFromChange(date)}
            />
            <CalendarInput
              nomarg
              labelText='To'
              minDate={from || new Date()}
              defaultValue={to}
              onChange={(date: Date | null) => setTo(date)}
            />
            <HonestSelectInput
              width='180px'
              nomarg
              labelText='Gender'
              options={genderOptions}
              defaultValue={gender}
              onChange={(v: ISelectValue) => setGender(v ? Number(v.value) : null)}
            />
            <HonestSelectInput
              width='180px'
              nomarg
              labelText='Tier'
              options={tierOptions}
              defaultValue={tier}
              onChange={(v: ISelectValue) => setTier(v ? Number(v.value) : null)}
            />
            <Button variant='primary' disabled={!from || !to} onClick={handleCheck}>
              Check availability
            </Button>
            {searched && (
              <Button variant='secondary' onClick={clearResults}>
                Reset
              </Button>
            )}
          </ControlsRow>
          <Caption>
            Shows beds free for the entire selected window — a bed occupied now but free during
            these dates appears as available.
          </Caption>
        </ContainerContent>
      </Container>

      {!searched ? (
        <EmptyState>Pick dates to see availability.</EmptyState>
      ) : isLoading ? (
        <LoaderWrap>
          <JustSpinner />
        </LoaderWrap>
      ) : (
        <>
          <KpiRow>
            <MetricCard
              layout='inline'
              label={
                range
                  ? `Beds available for ${formatDateForDisplay(range.from)}–${formatDateForDisplay(
                      range.to,
                    )}`
                  : 'Beds available'
              }
              value={String(available.length)}
              icon={<BedIcon />}
              accentColor={available.length ? tokens.status.success.fg : tokens.text.muted}
            />
            <MetricCard
              layout='inline'
              label='Camps with availability'
              value={String(grouped.length)}
              icon={<OccupiedBedIcon />}
              accentColor={tokens.status.info.fg}
            />
            <MetricCard
              layout='inline'
              label='Rooms with free beds'
              value={String(grouped.reduce((acc, c) => acc + c.rooms.length, 0))}
              icon={<OccupiedBedIcon />}
              accentColor={tokens.accent.base}
            />
          </KpiRow>

          {grouped.length > 0 && (
            <CampSummaryRow>
              {grouped.map((camp) => (
                <CampSummaryChip key={camp.campName}>
                  {camp.campName}
                  <CampSummaryCount>{camp.count}</CampSummaryCount>
                </CampSummaryChip>
              ))}
            </CampSummaryRow>
          )}

          {available.length ? (
            <ResultStack>
              {grouped.map((camp) => (
                <Container key={camp.campName}>
                  <ContainerHeader padding>
                    <CampGroupTitle>
                      <LocationIcon />
                      <CampGroupName>{camp.campName}</CampGroupName>
                      <StatusPill status='success'>{camp.count} free</StatusPill>
                    </CampGroupTitle>
                  </ContainerHeader>
                  <ContainerContent noHeader>
                    {camp.rooms.map((room) => (
                      <RoomGroup key={`${room.blockName}-${room.roomName}`}>
                        <RoomGroupHead>
                          <RoomGroupName>{room.roomName}</RoomGroupName>
                          <RoomGroupContext>
                            {room.blockName} · {genderLabel(room.gender)} · {tierLabel(room.tier)}
                          </RoomGroupContext>
                        </RoomGroupHead>
                        <BedGrid>
                          {room.beds.map((bed) => (
                            <BedCard key={bed.id}>
                              <BedCardTop>
                                <BedCardLabel>{bed.label}</BedCardLabel>
                                <StatusPill status='success' dot>
                                  Available
                                </StatusPill>
                              </BedCardTop>
                              <BedCardLocation>
                                <LocationIcon />
                                {bed.campName || '/'}
                                <NextIcon />
                                {bed.blockName || '/'}
                                <NextIcon />
                                {bed.roomName || '/'}
                              </BedCardLocation>
                              <PolicyPills>
                                <StatusPill status='neutral'>
                                  {genderLabel(bed.blockGenderPolicy)}
                                </StatusPill>
                                <StatusPill status='neutral'>
                                  {tierLabel(bed.blockTierPolicy)}
                                </StatusPill>
                              </PolicyPills>
                              <BedCardActions>
                                <Button
                                  variant='primary'
                                  size='sm'
                                  onClick={() =>
                                    setAssignBed({
                                      bedId: bed.id,
                                      label: bed.label,
                                      location: `${bed.roomName} · ${bed.blockName} · ${bed.campName}`,
                                    })
                                  }
                                >
                                  Assign
                                </Button>
                              </BedCardActions>
                            </BedCard>
                          ))}
                        </BedGrid>
                      </RoomGroup>
                    ))}
                  </ContainerContent>
                </Container>
              ))}
            </ResultStack>
          ) : (
            <EmptyState>No beds available for these dates and filters.</EmptyState>
          )}
        </>
      )}

      {assignBed && range && (
        <AssignBedModal
          closeModal={closeAssign}
          target={assignBed}
          from={range.from}
          to={range.to}
        />
      )}
    </>
  )
}
